import { Button, Input, Spinner, Container, Card, Badge } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaAngleLeft, FaAngleRight, FaCheck, FaEdit, FaFileImport, FaPlus, FaRegBuilding, FaSearch, FaTrash, FaUser, FaUsers } from 'react-icons/fa';
import { supplierActions } from '../../sagas/supplierSlice';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../../helpers/utility';
import { debounce } from 'lodash';
import { supplierDisplayName } from './SupplierInfoFields';
import './FournisseurList.css';

const FournisseurList = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { suppliers = [], loading } = useSelector(state => state.suppliers);
    const searchInputRef = useRef(null);
    const importInputRef = useRef(null);
    const [filter, setFilter] = useState('');
    const [status, setStatus] = useState('all');
    const [page, setPage] = useState(0);
    const pageSize = 25;
    const debouncedSearch = useMemo(() => debounce(query => dispatch(supplierActions.findAll({ query })), 350), [dispatch]);

    useEffect(() => {
        searchInputRef.current?.focus();
        const handleKeyDown = event => {
            if (event.key === 'F2') { event.preventDefault(); searchInputRef.current?.focus(); }
        };
        window.addEventListener('keydown', handleKeyDown);
        dispatch(supplierActions.findAll({ query: '' }));
        return () => { window.removeEventListener('keydown', handleKeyDown); debouncedSearch.cancel(); };
    }, [dispatch, debouncedSearch]);

    useEffect(() => { setPage(0); }, [filter, status]);
    const visibleSuppliers = suppliers.filter(supplier => status === 'all' || Boolean(supplier.active) === (status === 'active'));
    const totalPages = Math.max(1, Math.ceil(visibleSuppliers.length / pageSize));
    const currentPage = Math.min(page, totalPages - 1);
    const rows = visibleSuppliers.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
    const refresh = () => dispatch(supplierActions.findAll({ query: filter }));
    const handleEdit = fournisseur => navigate('/purchases-module/fournisseurs/' + fournisseur.id + '/edit', { state: { fournisseur } });
    const resetFilters = () => {
        debouncedSearch.cancel(); setFilter(''); setStatus('all');
        dispatch(supplierActions.findAll({ query: '' }));
    };

    const handleStatusChange = async supplier => {
        const active = Boolean(supplier.active);
        const result = await Swal.fire({
            title: active ? 'Désactiver le fournisseur ?' : 'Activer le fournisseur ?',
            text: active ? 'Le fournisseur sera désactivé et ne pourra plus être utilisé.' : 'Le fournisseur sera activé et pourra être utilisé.',
            icon: 'warning', showCancelButton: true, confirmButtonColor: active ? '#d33' : '#198754',
            confirmButtonText: active ? 'Désactiver' : 'Activer', cancelButtonText: 'Annuler'
        });
        if (!result.isConfirmed) return;
        dispatch((active ? supplierActions.inactivate : supplierActions.activate)({
            id: supplier.id,
            onSuccess: () => { refresh(); Swal.fire('Enregistré', active ? 'Le fournisseur a été désactivé.' : 'Le fournisseur a été activé.', 'success'); },
            onError: () => Swal.fire('Erreur', 'Impossible de modifier le statut du fournisseur.', 'error')
        }));
    };

    const handleImportFile = event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!['xlsx', 'xls'].includes(file.name.split('.').pop()?.toLowerCase())) {
            Swal.fire('Format invalide', 'Veuillez importer un fichier Excel .xlsx ou .xls avec les colonnes name et type.', 'warning');
            return;
        }
        dispatch(supplierActions.importSuppliers({ file,
            onSuccess: result => {
                const imported = result?.imported || 0;
                const skipped = result?.skipped || 0;
                const errors = result?.errors || [];
                Swal.fire({ icon: imported > 0 ? 'success' : 'warning', title: 'Import fournisseurs terminé',
                    text: imported + ' fournisseur(s) importé(s), ' + skipped + ' ligne(s) ignorée(s).' + (errors.length ? '\n' + errors.slice(0, 5).join('\n') : '') });
                refresh();
            },
            onError: () => Swal.fire('Erreur', "Impossible d'importer le fichier. Vérifiez les colonnes name et type.", 'error')
        }));
    };

    return <Container fluid className="supplier-list-page py-2">
        <Card className="supplier-list-panel">
            <header className="supplier-list-heading">
                <div className="supplier-list-title"><span className="supplier-list-icon"><FaUsers aria-hidden="true" /></span>
                    <div><h1>{t('nav.suppliers')}</h1><p>Coordonnées, soldes et fiches fournisseurs.</p></div>
                </div>
                <div className="supplier-list-header-actions">
                    <input ref={importInputRef} type="file" accept=".xlsx,.xls" className="d-none" onChange={handleImportFile} aria-label="Importer des fournisseurs depuis Excel" />
                    <Button size="sm" color="secondary" outline onClick={() => importInputRef.current?.click()} disabled={loading}><FaFileImport aria-hidden="true" /> Importer Excel</Button>
                    <Button size="sm" color="primary" onClick={() => navigate('/purchases-module/fournisseurs/new')}><FaPlus aria-hidden="true" /> {t('fournisseur.new')}</Button>
                </div>
            </header>
            <div className="supplier-list-toolbar">
                <div className="supplier-list-search"><FaSearch aria-hidden="true" />
                    <Input type="search" innerRef={searchInputRef} placeholder={t('fournisseur.searchPlaceholder')} aria-label={t('fournisseur.searchPlaceholder')} value={filter}
                        onChange={event => { setFilter(event.target.value); debouncedSearch(event.target.value); }}
                        onKeyDown={event => { if (event.key === 'Enter') { debouncedSearch.cancel(); refresh(); } }} />
                    <kbd>F2</kbd>
                </div>
                <Input className="supplier-list-status-filter" type="select" value={status} onChange={event => setStatus(event.target.value)} aria-label="Filtrer par statut">
                    <option value="all">Tous les statuts</option><option value="active">Actifs</option><option value="inactive">Bloqués</option>
                </Input>
                <span className="supplier-list-count" role="status">{visibleSuppliers.length} fournisseur{visibleSuppliers.length > 1 ? 's' : ''}</span>
            </div>
            <div className="supplier-list-table-wrap" aria-busy={loading}>
                <table className="table supplier-list-table">
                    <caption className="visually-hidden">{t('nav.suppliers')}</caption>
                    <thead><tr>
                        <th scope="col">{t('fournisseur.nom')}</th><th scope="col">{t('fournisseur.contact')}</th>
                        <th scope="col" className="supplier-number">{t('client.totalDebit')}</th><th scope="col" className="supplier-number">{t('client.totalCredit')}</th>
                        <th scope="col" className="supplier-number">{t('client.solde')}</th><th scope="col">{t('client.active')}</th><th scope="col" className="text-end">{t('common.actions')}</th>
                    </tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan="7" className="supplier-list-empty"><Spinner size="sm" color="primary" /><span role="status">{t('common.loading')}</span></td></tr> : rows.map(sup => {
                            const name = supplierDisplayName(sup) || sup.nom || sup.code || '—';
                            return <tr key={sup.id}>
                                <td><div className="supplier-list-identity"><span className="supplier-row-icon">{sup.typeFournisseur === 'ENTREPRISE' ? <FaRegBuilding aria-hidden="true" /> : <FaUser aria-hidden="true" />}</span>
                                    <div><button type="button" className="supplier-name-link" onClick={() => handleEdit(sup)}>{name}</button><small>{[sup.code, sup.typeFournisseur === 'PARTICULIER' ? 'Particulier' : 'Entreprise'].filter(Boolean).join(' · ')}</small></div>
                                </div></td>
                                <td><div className="supplier-list-contact"><span>{sup.telephone1 || '—'}</span><small>{sup.telephone2 || sup.email || '—'}</small></div></td>
                                <td className="supplier-number">{formatPrice(sup.totalDebit || 0)}</td>
                                <td className="supplier-number">{formatPrice(sup.totalCredit || 0)}</td>
                                <td className="supplier-number fw-semibold">{Number(sup.solde) < 0 ? <span className="text-success">+{formatPrice(Math.abs(sup.solde))}</span> : Number(sup.solde) > 0 ? <span className="text-danger">-{formatPrice(Math.abs(sup.solde))}</span> : formatPrice(0)}</td>
                                <td><Badge pill className={sup.active ? 'supplier-status-active' : 'supplier-status-inactive'}>{sup.active ? 'Actif' : 'Bloqué'}</Badge></td>
                                <td><div className="supplier-row-actions">
                                    <Button size="sm" color="primary" outline title="Modifier le fournisseur" aria-label={'Modifier ' + name} onClick={() => handleEdit(sup)}><FaEdit aria-hidden="true" /></Button>
                                    <Button size="sm" color={sup.active ? 'danger' : 'success'} outline title={sup.active ? 'Désactiver' : 'Activer'} aria-label={(sup.active ? 'Désactiver ' : 'Activer ') + name} onClick={() => handleStatusChange(sup)}>{sup.active ? <FaTrash aria-hidden="true" /> : <FaCheck aria-hidden="true" />}</Button>
                                </div></td>
                            </tr>;
                        })}
                        {!loading && rows.length === 0 && <tr><td colSpan="7" className="supplier-list-empty"><FaSearch aria-hidden="true" /><p>{t('fournisseur.noData')}</p>{(filter || status !== 'all') && <Button size="sm" color="link" onClick={resetFilters}>Réinitialiser les filtres</Button>}</td></tr>}
                    </tbody>
                </table>
            </div>
            <footer className="supplier-list-footer">
                <span>{visibleSuppliers.length ? currentPage * pageSize + 1 : 0}–{Math.min((currentPage + 1) * pageSize, visibleSuppliers.length)} sur {visibleSuppliers.length}</span>
                <div className="supplier-list-paging"><Button size="sm" color="light" aria-label="Page précédente" disabled={loading || currentPage === 0} onClick={() => setPage(currentPage - 1)}><FaAngleLeft aria-hidden="true" /></Button><span>Page {currentPage + 1} / {totalPages}</span><Button size="sm" color="light" aria-label="Page suivante" disabled={loading || currentPage >= totalPages - 1} onClick={() => setPage(currentPage + 1)}><FaAngleRight aria-hidden="true" /></Button></div>
            </footer>
        </Card>
    </Container>;
};

export default FournisseurList;
